import { computed, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';

import type { IPersona, IPersonaFilters } from '@rpgc-ai/shared-interfaces/interfaces/persona.interface';

import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { isEmpty, pickBy } from 'lodash-es';
import { distinctUntilChanged, map, tap } from 'rxjs';

import { initialPersonaState } from './persona.model';

export const PersonaStore = signalStore(
  { providedIn: 'root' },

  // Redux DevTools поддержка
  withDevtools('persona'),

  withState(initialPersonaState),
  withComputed((store) => {
    return {
      sanitizedFilters: computed<IPersonaFilters>(() => {
        const filters = store['filters']();
        const sanitized = { ...filters };

        Object.keys(sanitized).forEach((key) => {
          const filterKey = key as keyof IPersonaFilters;
          if (sanitized[filterKey] === null) {
            delete sanitized[filterKey];
          }
        });

        return sanitized;
      }),
    };
  }),
  withMethods((store) => {
    const router = inject(Router);
    const route = inject(ActivatedRoute);
    /**
     * Инициализация отслеживания query параметров
     * Вызывается автоматически при создании хранилища
     */
    const initRouteParamsSync = (): void => {
      // Слушаем изменения URL параметров и обновляем хранилище
      route.queryParams
        .pipe(
          map((params) => JSON.parse(params['persona_filters'] || '{}')),
          // убираем дубли запросов
          distinctUntilChanged((prev, curr) => {
            return JSON.stringify(prev) === JSON.stringify(curr);
          }),
          map((params) => {
            const sanitizedParams: Partial<IPersonaFilters> = {};

            // Обрабатываем строковые параметры
            if ('name' in params) {
              sanitizedParams.name = params['name'] || null;
            }

            return sanitizedParams;
          }),
          tap((sanitizedParams) => {
            // Используем patchState напрямую вместо метода store.setFilters
            updateState(store, '[Persona] Filters: Sync from Route', {
              filters: {
                ...store['filters'](),
                ...{ name: null },
                ...sanitizedParams,
              },
            });
          }),
        )
        .subscribe();
    };

    // Вызываем инициализацию сразу при создании хранилища
    initRouteParamsSync();
    return {
      /**
       * Устанавливает персонажей
       * @param personas
       */
      setPersonas(personas: IPersona[]) {
        updateState(store, '[Persona] Personas: Set', { personas });
      },
      setDefaultUserPersona(defaultUserPersona: IPersona) {
        updateState(store, '[Persona] Default User Persona: Set', { defaultUserPersona });
      },
      setPersona(selectedPersona: IPersona | null) {
        updateState(store, '[Persona] Persona: Select', { selectedPersona });
      },
      setSelectedPersonaId(selectedPersonaId: string | null) {
        updateState(store, '[Persona] Selected Persona Id: Set', { selectedPersonaId });
      },
      clearSelectedPersonaId() {
        updateState(store, '[Persona] Selected Persona Id: Clear', { selectedPersonaId: null });
      },
      setLoading(isLoading: boolean) {
        updateState(store, '[Persona] Loading: Set', { isLoading });
      },
      setError(error: string) {
        updateState(store, '[Persona] Error: Set', { error });
      },
      setNavigateOnSuccess(navigateOnSuccess: boolean) {
        updateState(store, '[Persona] Navigate On Success: Set', { navigateOnSuccess });
      },
      clear() {
        updateState(store, '[Persona] State: Clear', initialPersonaState);
      },
      /**
       * Устанавливает фильтры (без обновления URL)
       * @param filters
       */
      setFilters(filters: Partial<IPersonaFilters>) {
        const currentFilters = store['filters']();
        const newFilters = { ...currentFilters, ...filters };
        const defaultFilters = initialPersonaState.filters;
        const filteredFilters = pickBy(newFilters, (value) => {
          return !isEmpty(value);
        }) as Partial<IPersonaFilters>;

        updateState(store, '[Persona] Filters: Set', { filters: { ...defaultFilters, ...filteredFilters } });
      },

      /**
       * Устанавливает фильтры в роутер
       * @param filters
       */
      setFiltersRouter(filters: Partial<IPersonaFilters>) {
        const currentFilters = store['filters']();
        const newFilters = { ...currentFilters, ...filters };

        // Удаление null и пустых значений из объекта фильтров с использованием lodash
        const filteredFilters = pickBy(newFilters, (value) => {
          return !isEmpty(value);
        }) as Partial<IPersonaFilters>;

        const currentRoute = router.routerState.snapshot.url.split('?')[0];

        router.navigate([currentRoute], {
          queryParams: {
            persona_filters: JSON.stringify(filteredFilters),
          },
          queryParamsHandling: 'merge',
        });
      },
      setTotal(total: number) {
        updateState(store, '[Persona] Total: Set', { total });
      },
    };
  }),
);
